import { Component } from '@theme/component';
import { debounce, isClickedOutside, lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/** @extends Component<{dialog: HTMLDialogElement}> */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];
  #openFrame;
  #closeTimer;
  #generation = 0;
  #opened = false;
  #closing = false;
  #previousScrollY = 0;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('close', this.#nativeClose);
    this.refs.dialog.addEventListener('cancel', this.#nativeCancel);
    if (this.minWidth || this.maxWidth) window.addEventListener('resize', this.#handleResize);
  }

  disconnectedCallback() {
    ++this.#generation;
    if (this.#openFrame !== undefined) cancelAnimationFrame(this.#openFrame);
    this.#openFrame = undefined;
    clearTimeout(this.#closeTimer);
    const {dialog} = this.refs;
    dialog.removeEventListener('close', this.#nativeClose);
    dialog.removeEventListener('cancel', this.#nativeCancel);
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    window.removeEventListener('resize', this.#handleResize);
    this.#closing = false;
    this.#opened = false;
    dialog.classList.remove('dialog-closing');
    if (dialog.open) dialog.close();
    unlockScroll(dialog);
    super.disconnectedCallback();
  }

  #handleResize = debounce(() => {
    if (!this.isConnected) return;
    if ((this.minWidth && window.innerWidth < this.minWidth) || (this.maxWidth && window.innerWidth > this.maxWidth)) this.closeDialog();
  }, 50);

  showDialog() {
    const {dialog} = this.refs;
    if (!this.isConnected || dialog.open || this.#openFrame !== undefined) return;
    const generation = ++this.#generation;
    this.#previousScrollY = getScrollTop();
    this.#openFrame = requestAnimationFrame(() => {
      this.#openFrame = undefined;
      if (!this.isConnected || generation !== this.#generation || dialog.open) return;
      // Do not acquire a lock if the native showModal call fails.
      dialog.showModal();
      lockScroll(dialog);
      this.#opened = true;
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
      this.dispatchEvent(new DialogOpenEvent());
    });
  }

  #finishClose = (generation) => {
    if (generation !== this.#generation) return;
    const {dialog} = this.refs;
    const wasOpen = this.#opened || this.#closing || dialog.open;
    clearTimeout(this.#closeTimer);
    this.#closeTimer = undefined;
    this.#opened = false;
    this.#closing = false;
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    dialog.classList.remove('dialog-closing');
    if (dialog.open) dialog.close();
    unlockScroll(dialog);
    if (wasOpen) {
      scrollTo({top:this.#previousScrollY, behavior:'instant'});
      this.dispatchEvent(new DialogCloseEvent());
    }
  };

  #nativeClose = () => {
    // A queued native close event may belong to an earlier open cycle.
    if (this.refs.dialog.open) return;
    this.#finishClose(this.#generation);
  };

  #nativeCancel = (event) => {
    event.preventDefault();
    this.closeDialog();
  };

  closeDialog = async () => {
    if (this.#openFrame !== undefined) {
      cancelAnimationFrame(this.#openFrame);
      this.#openFrame = undefined;
      ++this.#generation;
    }
    const {dialog} = this.refs;
    if (this.#closing) return;
    const generation = ++this.#generation;
    if (!dialog.open) {
      this.#finishClose(generation);
      return;
    }
    this.#closing = true;
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    dialog.style.animation = 'none';
    void dialog.offsetWidth;
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';
    // Cleanup is bounded even if a browser never reports animation completion.
    this.#closeTimer = setTimeout(() => this.#finishClose(generation), 1200);
    try {
      await onAnimationEnd(dialog, undefined, {subtree:false});
    } finally {
      this.#finishClose(generation);
    }
  };

  toggleDialog = () => {
    if (this.refs.dialog.open || this.#openFrame !== undefined) this.closeDialog();
    else this.showDialog();
  };

  #handleClick(event) {
    if (isClickedOutside(event, this.refs.dialog)) this.closeDialog();
  }

  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.closeDialog();
  }

  get minWidth() { return Number(this.getAttribute('dialog-active-min-width')); }
  get maxWidth() { return Number(this.getAttribute('dialog-active-max-width')); }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);
export class DialogOpenEvent extends CustomEvent {
  constructor() { super(DialogOpenEvent.eventName); }
  static eventName = 'dialog:open';
}
export class DialogCloseEvent extends CustomEvent {
  constructor() { super(DialogCloseEvent.eventName); }
  static eventName = 'dialog:close';
}
document.addEventListener('toggle', (event) => {
  if (event.target instanceof HTMLDetailsElement && event.target.hasAttribute('scroll-lock')) {
    if (event.target.open) lockScroll(event.target);
    else unlockScroll(event.target);
  }
}, {capture:true});
